import React from "react";
import { DotLegalButton, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useLegalEntityAuditTabDataMapping } from "./LegalEntityAuditTab.hooks";
import { Box } from "@mui/material";
import { WhiteLockIcon } from "../../common/icons";
import SendAuditDialog from "./sendAuditDialog/SendAuditDialog";
import RegisterAuditReportDialog from "./registerAuditReportDialog/RegisterAuditReportDialog";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import PlanDialogButtonWrapper from "../../plan/planDialogWrapper/planDialogWrapper.tsx";
import { GetLegalEntityAuditPlanLimit } from "../../auth/planProvider/PlanProvider.hooks.ts";
import ExternalAuditTable from "../../common/components/questionnaireComponents/externalAuditTable/ExternalAuditTable";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery.ts";
import { getLegalEntityQueryKey } from "../LegalEntity.hooks.ts";

export interface ILegalEntityAuditProps {
    legalEntityId: string | undefined;
    legalEntityName: string | undefined;
    onAuditsChanged: () => void;
}

function LegalEntityAudit(props: ILegalEntityAuditProps) {
    const { translateString } = useTranslation();
    const { vendorManagementFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { refetchQueriesByPredicate } = useDotLegalQueryClient();

    const {
        onSendAuditClick,
        isLoading,
        audits,
        showDialog,
        setShowDialog,
        onAuditSent,
        refetchAudits,
        showCopyDialog,
        setShowCopyDialog,
        selectedAudit,
        onAuditCopied,
        showRegisterDialog,
        setShowRegisterDialog,
    } = useLegalEntityAuditTabDataMapping(props.legalEntityId, props.legalEntityName!, props.onAuditsChanged);

    const legalEntityPermissions = permissions.companyPermissions;
    const { legalEntityAuditPlanLimitIsLoading, refetchLegalEntityAuditPlanLimit, legalEntityAuditPlanLimitData } = GetLegalEntityAuditPlanLimit();

    return (
        <DotLegalPaper>
            <ExternalAuditTable
                data={audits}
                isLoading={isLoading}
                refetch={() => {
                    refetchAudits();
                    refetchLegalEntityAuditPlanLimit();
                }}
                pageType="legalEntity"
                emptyStateText={translateString("noLegalEntityAudits")}
                planLimit={legalEntityAuditPlanLimitData}
                legalEntityId={props.legalEntityId}
                legalEntityName={props.legalEntityName}
                sessionStorageKey={`legal-entity-${props.legalEntityId}-external-audits-filters`}
            />

            {vendorManagementFeatures.hasVendorManagement && legalEntityPermissions.create && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <PlanDialogButtonWrapper
                        enabled={legalEntityAuditPlanLimitData !== undefined && legalEntityAuditPlanLimitData.limitReached}
                        text={translateString("auditLimitReached")}
                    >
                        <DotLegalButton
                            buttonType={"secondary"}
                            onClick={() => setShowRegisterDialog(true)}
                            disabled={legalEntityAuditPlanLimitData?.limitReached}
                            endIcon={legalEntityAuditPlanLimitData?.limitReached ? WhiteLockIcon : undefined}
                            isLoading={legalEntityAuditPlanLimitIsLoading}
                            toolTip={legalEntityAuditPlanLimitData?.limitReached ? translateString("auditLimitReached") : undefined}
                        >
                            {translateString("registerAuditReport")}
                        </DotLegalButton>
                    </PlanDialogButtonWrapper>
                    <PlanDialogButtonWrapper
                        enabled={legalEntityAuditPlanLimitData !== undefined && legalEntityAuditPlanLimitData.limitReached}
                        text={translateString("auditLimitReached")}
                    >
                        <DotLegalButton
                            buttonType={"primary"}
                            onClick={onSendAuditClick}
                            disabled={legalEntityAuditPlanLimitData?.limitReached}
                            endIcon={legalEntityAuditPlanLimitData?.limitReached ? WhiteLockIcon : undefined}
                            isLoading={legalEntityAuditPlanLimitIsLoading}
                            toolTip={legalEntityAuditPlanLimitData?.limitReached ? translateString("auditLimitReached") : undefined}
                        >
                            {translateString("sendAudit")}
                        </DotLegalButton>
                    </PlanDialogButtonWrapper>
                </Box>
            )}

            {showDialog && (
                <SendAuditDialog
                    legalEntityId={props.legalEntityId}
                    legalEntityName={props.legalEntityName!}
                    onAuditSent={() => {
                        onAuditSent();
                        refetchLegalEntityAuditPlanLimit();
                    }}
                    onDialogClose={() => setShowDialog(false)}
                    planLimitData={legalEntityAuditPlanLimitData}
                />
            )}

            {showCopyDialog && (
                <SendAuditDialog
                    onDialogClose={() => setShowCopyDialog(false)}
                    onAuditSent={() => {
                        onAuditCopied();
                        setShowCopyDialog(false);
                    }}
                    legalEntityId={props.legalEntityId}
                    legalEntityName={props.legalEntityName!}
                    questionnaire={{
                        id: selectedAudit!.questionnaireId,
                        name: selectedAudit!.questionnaireName,
                    }}
                    responseId={selectedAudit!.questionnaireResponseId ?? undefined}
                    planLimitData={legalEntityAuditPlanLimitData}
                />
            )}

            {showRegisterDialog && (
                <RegisterAuditReportDialog
                    legalEntityId={props.legalEntityId!}
                    onDialogClose={() => setShowRegisterDialog(false)}
                    onAuditRegistered={() => {
                        setShowRegisterDialog(false);
                        refetchAudits();
                        refetchLegalEntityAuditPlanLimit();
                        if (props.legalEntityId) {
                            refetchQueriesByPredicate((query) =>
                                query.queryKey.some((key) => typeof key === "string" && key.includes(getLegalEntityQueryKey(props.legalEntityId!)))
                            );
                        }
                    }}
                />
            )}
        </DotLegalPaper>
    );
}

export default LegalEntityAudit;
